import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { SkillAttachmentList } from '../skills/SkillAttachmentList';
import { SkillPicker } from '../skills/SkillPicker';
import { useAttachToPersona, useDetachFromPersona, useSkillsForPersona } from '../../hooks/useSkills';

const SkillsPanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { currentPersonaId } = useFabricationStore();
  const [showPicker, setShowPicker] = useState(false);

  const { data: attachments = [] } = useSkillsForPersona(currentPersonaId ?? '');
  const attachMutation = useAttachToPersona();
  const detachMutation = useDetachFromPersona();

  const currentSkillNames = attachments.map((a) => a.skill_name);

  const handleAttach = (skillNames: string[]) => {
    if (!currentPersonaId) return;
    attachMutation.mutate({ personaId: currentPersonaId, skillNames });
  };

  const handleDetach = (skillName: string) => {
    if (!currentPersonaId) return;
    detachMutation.mutate({ personaId: currentPersonaId, skillName });
  };

  if (!currentPersonaId) {
    return (
      <div className={`text-center py-12 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Save the persona first to attach skills.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Skills</h2>
        <button
          onClick={() => setShowPicker(true)}
          className={`px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${
            darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          + Add Skill
        </button>
      </div>

      <div className={`p-4 rounded-lg border mb-4 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
        <p className={`text-xs mb-3 font-medium uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Attached Skills ({currentSkillNames.length})
        </p>
        <SkillAttachmentList
          entityType="persona"
          entityId={currentPersonaId}
          onDetach={handleDetach}
        />
      </div>

      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Skills are injected into the agent's system prompt at runtime. They provide behavioral guidelines
        and procedural knowledge without modifying the persona definition.
      </div>

      <SkillPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onAttach={handleAttach}
        currentSkills={currentSkillNames}
      />
    </div>
  );
};

export default SkillsPanel;
